import React, { useState, useEffect, useCallback, useRef } from "react";
import { GoDotFill } from "react-icons/go";
import {
  MdTrendingUp, MdTrendingDown, MdShoppingCart, MdEmojiEvents,
  MdCancel, MdAccountBalanceWallet, MdStorefront, MdPhoneAndroid,
  MdBarChart, MdPeople, MdCalendarToday, MdRefresh, MdSportsEsports,
} from "react-icons/md";
import { RiMoneyRupeeCircleLine } from "react-icons/ri";
import DashboardServices from "../../../services/DashboardServices";

// ── Date helpers ──────────────────────────────────────────────────────────────
const toDateStr = (d) => d.toISOString().slice(0, 10);

const getPresetRange = (preset) => {
  const today = new Date();
  const todayStr = toDateStr(today);
  if (preset === "today") return { fromDate: todayStr, toDate: todayStr };
  if (preset === "week") {
    const day = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((day + 6) % 7));
    return { fromDate: toDateStr(monday), toDate: todayStr };
  }
  if (preset === "month") {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { fromDate: toDateStr(first), toDate: todayStr };
  }
  return { fromDate: todayStr, toDate: todayStr };
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const initialSummary = {
  add_balance: 0, deduct_balance: 0,
  purchase: 0, win: 0, cancel: 0, game_profit: 0,
};

const ONLINE_POLL_MS = 25000;

const ROLE_MODAL_KEYS = {
  outlet: { title: "Online Outlets", listKey: "outlet", icon: "🏪" },
  mobile: { title: "Online Mobile Users", listKey: "mobile", icon: "📱" },
};

function formatOnlineUserLine(u) {
  const name = (u?.name || "").trim() || "—";
  const un = (u?.username || "").trim() || "—";
  return { name, username: un };
}

const PRESET_LABELS = [
  { key: "today", label: "Today" },
  { key: "week",  label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom" },
];

const DashboardSuperAdmin = ({ data }) => {
  const stats    = data?.stats    || {};
  const games = data?.games || [];
  const recentWinners = data?.recentWinners || [];


  // ── Financial summary ────────────────────────────────────────────────────
  const [summaryPreset, setSummaryPreset] = useState("today");
  const [customFrom, setCustomFrom] = useState(toDateStr(new Date()));
  const [customTo,   setCustomTo]   = useState(toDateStr(new Date()));
  const [summary, setSummary]       = useState(initialSummary);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const summaryAbortRef = useRef(null);

  const fetchSummary = useCallback(async (preset, fromDate, toDate) => {
    summaryAbortRef.current?.abort();
    const ac = new AbortController();
    summaryAbortRef.current = ac;
    setSummaryLoading(true);
    try {
      const range = preset === "custom" ? { fromDate, toDate } : getPresetRange(preset);
      const res = await DashboardServices.getSuperAdminReportSummary(range);
      if (ac.signal.aborted) return;
      if (res?.isSuccess && res?.data) setSummary(res.data);
    } catch (e) {
      if (e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;
    } finally {
      if (!ac.signal.aborted) setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary(summaryPreset, customFrom, customTo);
    return () => summaryAbortRef.current?.abort();
  }, []); // eslint-disable-line

  const handlePreset = (p) => {
    setSummaryPreset(p);
    if (p !== "custom") fetchSummary(p, customFrom, customTo);
  };
  const handleCustomApply = () => {
    if (customFrom && customTo && customFrom <= customTo)
      fetchSummary("custom", customFrom, customTo);
  };

  // ── Online users ─────────────────────────────────────────────────────────
  const [onlinePayload, setOnlinePayload] = useState({
    counts: { admin: 0, outlet: 0, mobile: 0 },
    users:  { admin: [], outlet: [], mobile: [] },
  });
  const [onlineModalRole, setOnlineModalRole] = useState(null);
  const onlineAbortRef = useRef(null);

  const loadOnlineUsers = useCallback(async () => {
    try {
      onlineAbortRef.current?.abort();
      const ac = new AbortController();
      onlineAbortRef.current = ac;
      const res = await DashboardServices.getOnlineUsersByRole({ signal: ac.signal });
      if (res?.isSuccess && res?.data) {
        setOnlinePayload({
          counts: res.data.counts || { admin: 0, outlet: 0, mobile: 0 },
          users:  res.data.users  || { admin: [], outlet: [], mobile: [] },
        });
      }
    } catch (e) {
      if (e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;
    }
  }, []);

  useEffect(() => {
    loadOnlineUsers();
    const t = setInterval(loadOnlineUsers, ONLINE_POLL_MS);
    return () => { clearInterval(t); onlineAbortRef.current?.abort(); };
  }, [loadOnlineUsers]);



  const liveCount = games.filter((g) => g.is_active).length;
  const modalMeta = onlineModalRole ? ROLE_MODAL_KEYS[onlineModalRole] : null;
  const modalList = modalMeta ? (onlinePayload.users[modalMeta.listKey] || []) : [];
  const isProfit  = Number(summary.game_profit) >= 0;

  // ── Finance cards config ─────────────────────────────────────────────────
  const financeCards = [
    {
      key: "add_balance", label: "Add Balance",
      value: summary.add_balance, icon: <MdTrendingUp size={22} />,
      cls: "saf-card--add",
    },
    {
      key: "deduct_balance", label: "Deduct Balance",
      value: summary.deduct_balance, icon: <MdTrendingDown size={22} />,
      cls: "saf-card--deduct",
    },
    {
      key: "purchase", label: "Purchase",
      value: summary.purchase, icon: <MdShoppingCart size={22} />,
      cls: "saf-card--purchase",
    },
    {
      key: "win", label: "Win",
      value: summary.win, icon: <MdEmojiEvents size={22} />,
      cls: "saf-card--win",
    },
    {
      key: "cancel", label: "Total Cancel",
      value: summary.cancel, icon: <MdCancel size={22} />,
      cls: "saf-card--cancel",
    },
    {
      key: "game_profit", label: "Game Profit",
      value: Math.abs(summary.game_profit),
      prefix: isProfit ? "+" : "−",
      icon: <RiMoneyRupeeCircleLine size={22} />,
      cls: isProfit ? "saf-card--profit" : "saf-card--loss",
    },
  ];

  return (
    <div className="sa-dashboard">

      {/* ── Row 1: KPI strip ─────────────────────────────────────────── */}
      <div className="sa-top-grid">
        <div className="sa-top-card sa-top-card--sessions">
          <div className="sa-top-card-icon-wrap sa-icon--bar"><MdBarChart size={24} /></div>
          <div className="sa-top-card-body">
            <div className="sa-top-card-label">Today Sessions</div>
            <div className="sa-top-card-value">{stats.todaySessionsCount ?? 0}</div>
            <div className="sa-top-card-sub">
              {liveCount} of {games.length} game{games.length === 1 ? "" : "s"} live
            </div>
          </div>
        </div>

        <div className="sa-top-card sa-top-card--winners">
          <div className="sa-top-card-icon-wrap sa-icon--trophy"><MdEmojiEvents size={24} /></div>
          <div className="sa-top-card-body">
            <div className="sa-top-card-label">Today Winners</div>
            <div className="sa-top-card-value">{stats.todayWinnersCount ?? 0}</div>
            <div className="sa-top-card-sub">rounds settled today</div>
          </div>
        </div>

        <div className="sa-top-card sa-top-card--users">
          <div className="sa-top-card-icon-wrap sa-icon--people"><MdPeople size={24} /></div>
          <div className="sa-top-card-body">
            <div className="sa-top-card-label">Total Users</div>
            <div className="sa-top-card-value">{stats.totalUsersCount ?? 0}</div>
            <div className="sa-top-card-sub">admins, outlets &amp; players</div>
          </div>
        </div>

        {/* Online now - the two counts open the user lists. */}
        <div className="sa-top-card sa-top-card--online">
          <div className="sa-top-card-icon-wrap sa-icon--online">
            <span className="sa-online-dot" />
          </div>
          <div className="sa-top-card-body">
            <div className="sa-top-card-label-row">
              <span className="sa-top-card-label">Online Now</span>
              <button
                type="button"
                className="sa-refresh-btn sa-refresh-btn--sm"
                onClick={loadOnlineUsers}
                title="Refresh online count"
              >
                <MdRefresh size={14} />
              </button>
            </div>
            <div className="sa-online-chips">
              <button
                type="button"
                className="sa-online-chip sa-online-chip--outlet"
                onClick={() => setOnlineModalRole("outlet")}
                title="Show online outlets"
              >
                <MdStorefront size={16} />
                <span className="sa-online-chip-count">{onlinePayload.counts.outlet ?? 0}</span>
                <span className="sa-online-chip-label">Outlets</span>
              </button>
              <button
                type="button"
                className="sa-online-chip sa-online-chip--mobile"
                onClick={() => setOnlineModalRole("mobile")}
                title="Show online mobile users"
              >
                <MdPhoneAndroid size={16} />
                <span className="sa-online-chip-count">{onlinePayload.counts.mobile ?? 0}</span>
                <span className="sa-online-chip-label">Mobile</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 2: Financial Summary ─────────────────────────────────── */}
      <div className="sa-section">
        <div className="sa-section-head">
          <div className="sa-section-title-row">
            <MdAccountBalanceWallet size={20} className="sa-section-icon" />
            <h2 className="sa-section-title">Financial Summary</h2>
            {summaryLoading && <span className="sa-spin-dot" />}
          </div>

          <div className="sa-filter-bar">
            {PRESET_LABELS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`sa-pill${summaryPreset === key ? " sa-pill--active" : ""}`}
                onClick={() => handlePreset(key)}
              >
                {label}
              </button>
            ))}
            {summaryPreset === "custom" && (
              <div className="sa-custom-range">
                <MdCalendarToday size={14} className="sa-cal-icon" />
                <input
                  type="date"
                  className="sa-range-input"
                  value={customFrom}
                  max={customTo}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
                <span className="sa-range-sep">→</span>
                <input
                  type="date"
                  className="sa-range-input"
                  value={customTo}
                  min={customFrom}
                  max={toDateStr(new Date())}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
                <button
                  type="button"
                  className="sa-pill sa-pill--apply"
                  onClick={handleCustomApply}
                  disabled={!customFrom || !customTo || customFrom > customTo}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="sa-finance-grid">
          {financeCards.map((c) => (
            <div key={c.key} className={`saf-card ${c.cls}`}>
              <div className="saf-card-top">
                <span className="saf-icon">{c.icon}</span>
                <span className="saf-label">{c.label}</span>
              </div>
              <div className="saf-value">
                {c.prefix && <span className="saf-prefix">{c.prefix}</span>}
                ₹ {fmt(c.value)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Row 3: Games ─────────────────────────────────────────────── */}
      <div className="sa-section">
        <div className="sa-section-head">
          <div className="sa-section-title-row">
            <MdSportsEsports size={20} className="sa-section-icon" />
            <h2 className="sa-section-title">Games</h2>
            <span className="sa-count-pill">
              {liveCount} live · {games.length - liveCount} stopped
            </span>
          </div>
        </div>

        {games.length === 0 ? (
          <div className="sa-empty-state">
            <span className="sa-empty-icon">🎮</span>
            <p>No games configured. Add one in Settings › Games.</p>
          </div>
        ) : (
          <div className="sa-winners-table-wrap">
            <table className="sa-winners-table sa-games-table">
              <thead>
                <tr>
                  <th>Game</th>
                  <th>Status</th>
                  <th className="sa-num">Duration</th>
                  <th className="sa-num">Ghar</th>
                  <th className="sa-num">Rounds / day</th>
                  <th className="sa-num">Yantra Amount</th>
                  <th className="sa-num">Winning Amount</th>
                  <th className="sa-num">Difference</th>
                  <th className="sa-num">Bet Close</th>
                </tr>
              </thead>
              <tbody>
                {games.map((g) => {
                  const live = Boolean(g.is_active);
                  return (
                    <tr key={g.id} className={live ? "" : "sa-row--muted"}>
                      <td>
                        <div className="sa-game-cell">
                          <span className="sa-game-name">{g.game_name}</span>
                          <span className="sa-game-chip">{g.game_code}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`sa-game-badge ${live ? "sa-badge--live" : "sa-badge--stopped"}`}>
                          <GoDotFill size={10} />
                          {live ? "Live" : "Stopped"}
                        </span>
                      </td>
                      <td className="sa-num">{g.duration_minutes} min</td>
                      <td className="sa-num">{g.total_ghar}</td>
                      <td className="sa-num">{g.sessions_per_day}</td>
                      <td className="sa-num sa-money">₹ {g.yantra_amount}</td>
                      <td className="sa-num sa-money sa-money--win">₹ {g.winning_amount}</td>
                      <td className="sa-num">{g.difference_amount}</td>
                      <td className="sa-num sa-muted">{g.bet_close_seconds}s</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Row 4: Recent Winner Sessions ───────────────────────────── */}
      <div className="sa-section">
        <div className="sa-section-head">
          <div className="sa-section-title-row">
            <MdEmojiEvents size={20} className="sa-section-icon" />
            <h2 className="sa-section-title">Recent Winner Sessions</h2>
          </div>
        </div>

        {recentWinners.length === 0 ? (
          <div className="sa-empty-state">
            <span className="sa-empty-icon">🏆</span>
            <p>No recent winner sessions.</p>
          </div>
        ) : (
          <div className="sa-winners-table-wrap">
            <table className="sa-winners-table">
              <thead>
                <tr>
                  <th>Session</th>
                  <th>Game</th>
                  <th>Type</th>
                  <th>Winner Yantra</th>
                  <th>Win Amount</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {recentWinners.map((w) => {
                  const winnerType = w.winner_type || "NORMAL";
                  const isJackpot  = winnerType === "JACKPOT";
                  const isJackpotX = winnerType === "JACKPOTX";
                  return (
                    <tr key={w.id}>
                      <td className="sa-wt-session">#{w.session_no ?? "—"}</td>
                      <td>
                        <span className="sa-game-chip">
                          {w.game_type ?? "—"}
                        </span>
                      </td>
                      <td>
                        {isJackpot ? (
                          <span className="sa-type-badge sa-type--jackpot">Multi Winner</span>
                        ) : isJackpotX ? (
                          <span className="sa-type-badge sa-type--jackpotx">Jackpot {w.winner_x ? `(${w.winner_x}x)` : ""}</span>
                        ) : (
                          <span className="sa-type-badge sa-type--normal">Normal</span>
                        )}
                      </td>
                      <td className="sa-wt-yantras">
                        {Array.isArray(w.winner_yantra) && w.winner_yantra.length > 0
                          ? w.winner_yantra.join(" · ")
                          : "—"}
                      </td>
                      <td className="sa-wt-amount">
                        ₹ {Number(w.winning_amount || 0).toFixed(2)}
                      </td>
                      <td className="sa-wt-time">
                        {w.end_time
                          ? new Date(w.end_time).toLocaleString("en-IN", {
                              day: "2-digit", month: "short",
                              hour: "2-digit", minute: "2-digit",
                            })
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Online users modal ───────────────────────────────────────── */}
      {modalMeta && (
        <div
          className="sa-modal-backdrop"
          role="presentation"
          onClick={() => setOnlineModalRole(null)}
        >
          <div
            className="sa-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sa-modal-head">
              <span className="sa-modal-icon">{modalMeta.icon}</span>
              <h3 className="sa-modal-title">{modalMeta.title}</h3>
              <button
                type="button"
                className="sa-modal-close"
                onClick={() => setOnlineModalRole(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <ul className="sa-modal-list">
              {modalList.length === 0 ? (
                <li className="sa-modal-empty">No users online.</li>
              ) : (
                modalList.map((u) => {
                  const { name, username } = formatOnlineUserLine(u);
                  return (
                    <li key={u.user_id} className="sa-modal-item">
                      <span className="sa-modal-avatar">{(name[0] || "?").toUpperCase()}</span>
                      <div className="sa-modal-user-info">
                        <span className="sa-modal-user-name">{name}</span>
                        <span className="sa-modal-user-id">{username}</span>
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardSuperAdmin;
